import { useContext, useState } from "react";
import { Response } from "../../domain/app/Response";
import { AppTimestamp } from "../../domain/app/Timestamp";
import { UserAuthContext } from "../../ui/context/UserContext";
import {
  GoodsControl,
  NewGoodsControl,
  PutGoodsControl,
  UserGoodsControl,
} from "../../domain/control/Goods";
import { GoodsControlRepository } from "../../data/repository/GoodsControlRepository";
import { LocalErrorLogRepository } from "../../data/repository/LocalErrorLogRepository";
import { generateExcel } from "../../util/generateExcel";
import { orderBy } from "lodash";

type FormModalValues = {
  [key: string]: any;
};

export function useGoodsControlViewModel() {
  const [editEvent, setEditEvent] = useState<GoodsControl | null>(null);
  const [editState, setEditState] = useState<Response<GoodsControl> | null>(
    null
  );

  const [createEvent, setCreateEvent] = useState<boolean | null>(null);
  const [createState, setCreateState] = useState<Response<boolean> | null>(
    null
  );

  const [fetchState, setFetchState] = useState<Response<boolean> | null>(null);
  const [goodsControlList, setGoodsControlList] = useState<
    UserGoodsControl[] | undefined
  >();
  const [goodsControlTimestamp, setGoodsControlTimestamp] = useState<
    AppTimestamp | undefined
  >();
  const [refreshEvent, setRefreshEvent] = useState<boolean | null>(null);
  const { appUser } = useContext(UserAuthContext);

  // search
  const [searchEvent, setSearchEvent] = useState<boolean | null>(null);
  const [searchState, setSearchState] = useState<Response<boolean> | null>(
    null
  );
  const [searchResults, setSearchResults] = useState<UserGoodsControl[] | null>(
    null
  );

  // generate excel
  const [generateEvent, setGenerateEvent] = useState<boolean | null>(null);

  const [generateState, setGenerateState] = useState<Response<boolean> | null>(
    null
  );
  // modal alert
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [descriptionAlert, setDescriptionAlert] = useState("");

  function requestCreateEvent() {
    setCreateEvent(true);
  }

  function onCreateEventCompleted() {
    setCreateEvent(null);
  }

  async function createGoodsControl(control: NewGoodsControl) {
    if (createState?.loading) return;
    setCreateState(Response.loading());
    try {
      await GoodsControlRepository.create({
        ...control,
        ownerUid: appUser.uid(),
        unityId: appUser.user.unityId!,
      });
      setCreateState(Response.success(true));
    } catch (e: any) {
      const label = `Error al crear el registro de bienes.'`;
      console.log(e);
      await LocalErrorLogRepository.registerError(label, e);
      setCreateState(Response.failure(new Error(label)));
    }
  }

  function onCreateStateReceived() {
    setCreateState(null);
  }

  function requestEditEvent(vehicleControl: GoodsControl) {
    setEditEvent(vehicleControl);
  }

  function onEditEventCompleted() {
    setEditEvent(null);
  }

  async function editVehicleControl(newData: PutGoodsControl) {
    if (editState?.loading) return;
    setEditState(Response.loading());
    try {
      if (!editEvent) {
        setEditState(
          Response.failure(new Error("El control de bienes no es válido."))
        );
        return;
      }
      const result = await GoodsControlRepository.update(
        editEvent.reference,
        newData,
        editEvent
      );
      setEditState(Response.success(result));
    } catch (e: any) {
      setEditState(Response.failure(e));
    }
  }

  function onEditStateReceived() {
    setEditState(null);
  }

  async function fetchGoodsControlList(
    timestamp: number,
    forceRefresh: boolean = false
  ) {
    if (fetchState?.isLoading()) return;
    setFetchState(Response.loading());
    try {
      const result = await GoodsControlRepository.getGlobal(
        {
          orderBy: "fecha",
          order: "desc",
        },
        Date.now(),
        0,
        false,
        appUser
      );
      setGoodsControlList(result?.goodsControlList);
      setGoodsControlTimestamp(result?.timestamp);
      setFetchState(Response.success(true));
    } catch (e: any) {
      setFetchState(Response.failure(e));
    }
  }

  async function searchGoodsControl(values: FormModalValues) {
    setFetchState(Response.loading());
    try {
      const results = await GoodsControlRepository.searchGoodsControl(
        values.filter,
        values.search,
        values.searchInAll === true ? null : values.dpInicio.getTime(),
        values.searchInAll === true ? null : values.dpFinal.getTime(),
        values.searchInAll
      );
      console.log("searchResult", results);
      setSearchResults(results || []);
      setFetchState(Response.success(true));
    } catch (e: any) {
      const label = `Error al buscar el control de bienes: '${values.search}'`;
      await LocalErrorLogRepository.registerError(label, e);
      setFetchState(Response.failure(new Error(label)));
    }
  }

  async function toGenerateExcel() {
    setFetchState(Response.loading());
    try {
      console.log("to generate excel");
      console.log("search results", searchResults);
      if (searchResults?.length !== undefined && searchResults?.length > 0) {
        generateExcel(searchResults, "GoodsControl");
      } else {
        setIsAlertVisible(true);
        setDescriptionAlert(
          "Se necesita obtener resultados para generar Excel"
        );
      }

      setFetchState(Response.success(true));
    } catch (e: any) {
      const label = `Error al generar excel: ''`;
      await LocalErrorLogRepository.registerError(label, e);
      setFetchState(Response.failure(new Error(label)));
    }
  }

  function onFetchStateReceived() {
    setFetchState(null);
  }

  function requestRefreshEvent() {
    setRefreshEvent(true);
  }

  function onRefreshEventCompleted() {
    setRefreshEvent(null);
  }
  function requestSearchEvent() {
    setSearchEvent(true);
  }

  function onSearchEventCompleted() {
    setSearchEvent(null);
    setSearchResults(null);
  }
  return {
    //fetch
    fetchState,
    onFetchStateReceived,
    goodsControlList,
    goodsControlTimestamp,
    fetchGoodsControlList,
    //refresh
    refreshEvent,
    requestRefreshEvent,
    onRefreshEventCompleted,

    //search
    searchEvent,
    searchState,
    searchResults,
    requestSearchEvent,
    onSearchEventCompleted,
    searchGoodsControl,

    //generate
    toGenerateExcel,

    //alert
    isAlertVisible,
    setIsAlertVisible,
    descriptionAlert,

    createState,
    createEvent,
    onCreateEventCompleted,
    onCreateStateReceived,
    createGoodsControl,
    requestCreateEvent,
    editEvent,
    editState,
    onEditEventCompleted,
    onEditStateReceived,
    editVehicleControl,
    requestEditEvent,
  };
}
